import { create } from 'zustand';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { clearLocalData, db, kvGet, kvSet } from '@/lib/db';
import { ensureScope } from '@/lib/sync';
import { fallbackAccess, type Access, type ModuleKey } from '@/lib/access';
import type { Profile, UserRole } from '@/types/models';

const ACCESS_KEY = 'access';
const BRANCH_KEY = 'suremix.branch';

/** Role capability matrix - section 27 of the specification.
 *  Mirrors the RLS policies in migration 0015. The server enforces;
 *  this only decides what the UI offers. */
const CAPABILITIES: Record<UserRole, string[]> = {
  operator: ['diary:create', 'diary:edit-own'],
  supervisor: [
    'diary:create', 'diary:edit-own', 'diary:edit-all', 'diary:approve',
    'production:edit', 'blast:edit', 'weather:edit', 'breakdown:edit',
  ],
  maintenance: [
    'breakdown:edit', 'service:edit', 'liner:edit', 'parts:edit', 'stock:edit',
  ],
  manager: [
    'diary:create', 'diary:edit-own', 'diary:edit-all', 'diary:approve',
    'production:edit', 'blast:edit', 'weather:edit', 'breakdown:edit',
    'service:edit', 'liner:edit', 'parts:edit', 'stock:edit',
    'targets:edit', 'reports:view', 'audit:view', 'masterdata:edit',
  ],
  admin: ['*'],
};

interface AuthState {
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  error: string | null;
  /** Branches and modules (0055), from my_access(); cached for offline. */
  access: Access | null;
  /** The branch a branch user is working in, when they have several. */
  selectedBranch: string | null;

  initialise: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  can: (capability: string) => boolean;
  hasRole: (...roles: UserRole[]) => boolean;
  /** May use this module (0055). The server enforces the same list. */
  hasModule: (module: ModuleKey) => boolean;
  /** The branch a branch user is working in; null for quarry users. */
  branchId: () => string | null;
  /** The branches a branch user works for (several possible, 0055). */
  memberBranches: () => string[];
  selectBranch: (siteId: string) => void;
  /** Re-read the profile after something server-side changed it — the
   *  forced password change clears a flag this store is gating on. */
  refreshProfile: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  session: null,
  profile: null,
  loading: true,
  error: null,
  access: null,
  selectedBranch: localStorage.getItem(BRANCH_KEY),

  async initialise() {
    const { data } = await supabase.auth.getSession();
    set({ session: data.session });

    supabase.auth.onAuthStateChange((_event, session) => {
      set({ session });
      if (session) void loadProfile(set);
      else set({ profile: null });
    });

    if (data.session) await loadProfile(set);
    set({ loading: false });
  },

  async signIn(email, password) {
    set({ loading: true, error: null });
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      set({ loading: false, error: error.message });
      throw error;
    }
    set({ session: data.session });
    await loadProfile(set);
    set({ loading: false });
  },

  async refreshProfile() {
    await loadProfile(set);
  },

  async signOut() {
    await supabase.auth.signOut();
    // Never leave one user's shift behind on a shared tablet.
    await clearLocalData();
    localStorage.removeItem(BRANCH_KEY);
    set({ session: null, profile: null, access: null, selectedBranch: null });
  },

  can(capability) {
    const role = get().profile?.role;
    if (!role || get().profile?.home_site_id) return false;
    const caps = CAPABILITIES[role];
    return caps.includes('*') || caps.includes(capability);
  },

  hasRole(...roles) {
    // A branch user's role is a role at their branch, never at the quarry —
    // the same rule as app.has_role() on the server (0035).
    const p = get().profile;
    if (!p?.role || p.home_site_id) return false;
    return roles.includes(p.role);
  },

  hasModule(module) {
    const { profile, access } = get();
    if (!profile || profile.is_active === false) return false;
    if (profile.role === 'admin') return true;
    return (access ?? fallbackAccess(profile.role, profile.home_site_id)).modules.includes(module);
  },

  branchId() {
    const home = get().profile?.home_site_id ?? null;
    if (!home) return null;
    const mine = get().memberBranches();
    const chosen = get().selectedBranch;
    return chosen && mine.includes(chosen) ? chosen : home;
  },

  memberBranches() {
    const { profile, access } = get();
    if (!profile) return [];
    const quarry = '00000000-0000-0000-0000-000000000001';
    const list = (access ?? fallbackAccess(profile.role, profile.home_site_id)).member_sites
      .filter((s) => s !== quarry);
    if (profile.home_site_id && !list.includes(profile.home_site_id)) list.unshift(profile.home_site_id);
    return list;
  },

  selectBranch(siteId) {
    localStorage.setItem(BRANCH_KEY, siteId);
    set({ selectedBranch: siteId });
  },
}));

/** Read the profile from the server when online, otherwise from the local
 *  mirror - a supervisor starting the app offline must still get their role. */
async function loadProfile(set: (partial: Partial<AuthState>) => void): Promise<void> {
  const { data: sessionData } = await supabase.auth.getSession();
  const userId = sessionData.session?.user.id;
  if (!userId) return;

  if (navigator.onLine) {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();
    if (!error && data) {
      const profile = data as Profile;
      await db.profiles.put(profile);
      const access = await loadAccess(profile);
      // Moved between branches, given or refused a module, or into or out
      // of the workshop's cost tables since this device last synced: rebuild.
      await ensureScope(profile.home_site_id, profile.role, access);
      set({ profile, access });
      return;
    }
  }

  const cached = await db.profiles.get(userId);
  if (cached) {
    const access = (await kvGet<Access>(ACCESS_KEY)) ?? fallbackAccess(cached.role, cached.home_site_id);
    set({ profile: cached, access });
  }
}

/** my_access() (0055). Before that migration is deployed, or if the call
 *  fails, the profile alone decides — exactly the app's earlier behaviour. */
async function loadAccess(profile: Profile): Promise<Access> {
  const { data, error } = await supabase.rpc('my_access');
  const ok = !error && !!data && typeof data === 'object' && !Array.isArray(data)
    && Array.isArray((data as Access).modules) && Array.isArray((data as Access).sites);
  const access = ok ? (data as Access) : fallbackAccess(profile.role, profile.home_site_id);
  await kvSet(ACCESS_KEY, access);
  return access;
}
